import React, { useEffect, useState } from "react";
import AddDetails from "./AddDetails";
import EditDetails from "./EditDetails";
import GroupApi from "../../api/GroupApi";
import { can } from "../../utils/permissionUtils";
import Auth from "../../utils/AuthUtils";

function List() {
  const [group, setGroup] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Active Logic: Get user permissions
  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  /* ================= FETCH DATA ================= */
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await GroupApi.list();
      if (res.code === 200) {
        setGroup(res.data);
      } else {
        setError(res.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  /* ================= DELETE LOGIC ================= */
  const handleDelete = async () => {
    try {
      // ✅ Security Logic: Matrix check
      if (!can(permissions, "delete", "group")) return;

      const res = await GroupApi.delete({ id: selectedGroup._id });
      if (res.code === 200) {
        const modalEl = document.getElementById("delete-group-modal");
        const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
        if (modalInstance) modalInstance.hide();
        fetchGroups();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const filteredGroup = group.filter((item) => {
    const search = searchTerm.toLowerCase().trim();

    return item.group?.toLowerCase().includes(search);
  });

  return (
    <div className="content pb-0">
      {/* ================= HEADER (UI from Commented Code) ================= */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-4 flex-wrap">
        <div>
          <h4 className="mb-1">
            Group Management{" "}
            <span className="badge badge-soft-primary ms-2">
              {filteredGroup.length}
            </span>
          </h4>
        </div>
      </div>

      <div className="gap-2 d-flex align-items-center mb-3 justify-content-between flex-wrap">
        {/* ✅ Active Logic: Check 'write' permission before showing Add Button */}

        <div className="group-search-box position-relative">
          <i className="ti ti-search search-icon"></i>

          <input
            type="text"
            className="form-control search-input"
            placeholder="Search by Group..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {can(permissions, "write", "group") && (
          <button
            type="button"
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#standard-modal"
          >
            <i className="ti ti-square-rounded-plus-filled me-1" />
            Add Group
          </button>
        )}
      </div>

      {/* ================= TABLE (UI from Commented Code) ================= */}
      <div className="card border-0 rounded-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive custom-table">
            <table className="table table-nowrap align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.no</th>
                  <th>Lead Group Title</th>
                  <th>Created on</th>
                  {/* ✅ Active Logic: Action column check */}
                  {(can(permissions, "write", "group") ||
                    can(permissions, "delete", "group")) && (
                    <th className="text-center">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="4" className="text-danger text-center py-4">
                      {error}
                    </td>
                  </tr>
                ) : (
                  filteredGroup.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td className="fw-bold text-dark">{item.group}</td>
                      <td>
                        {new Date(item.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center">
                          {/* ✅ Active Logic: Edit button check */}
                          {can(permissions, "write", "group") && (
                            <button
                              className="btn btn-soft-success btn-sm me-2"
                              data-bs-toggle="modal"
                              data-bs-target="#edit-group-modal"
                              onClick={() => setSelectedGroup(item)}
                            >
                              <i className="ti ti-edit" />
                            </button>
                          )}

                          {/* ✅ Active Logic: Delete button check */}
                          {can(permissions, "delete", "group") && (
                            <button
                              className="btn btn-soft-danger btn-sm"
                              data-bs-toggle="modal"
                              data-bs-target="#delete-group-modal"
                              onClick={() => setSelectedGroup(item)}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================= MODALS ================= */}
      <div
        id="standard-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
      >
        <AddDetails onAdded={fetchGroups} />
      </div>

      <div
        id="edit-group-modal"
        className="modal fade"
        tabIndex={-1}
        role="dialog"
      >
        <EditDetails
          groupData={selectedGroup}
          fetchGroups={fetchGroups}
          clearSelection={() => setSelectedGroup(null)}
        />
      </div>

      {/* Delete Confirmation Modal (UI from Commented Code) */}
      <div
        className="modal fade"
        id="delete-group-modal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-sm rounded-0">
          <div className="modal-content rounded-0">
            <div className="modal-body p-4 text-center position-relative">
              <div className="mb-3 position-relative z-1">
                <span className="avatar avatar-xl badge-soft-danger border-0 text-danger rounded-circle">
                  <i className="ti ti-trash fs-24"></i>
                </span>
              </div>
              <h5 className="mb-1">Confirm Delete</h5>
              <p className="small text-muted mb-3">
                Are you sure you want to remove this group?
              </p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  type="button"
                  className="btn btn-light w-100"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger w-100"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
